// JOBE-0162.1: Universal directory data layer.
//
// When the `wcc.mock.organization` mock flag is ON: returns the
// fused humans+agents fixture (canonical 25-row demo set).
//
// When the flag is OFF (default 2026-05-24): fetches `/api/agents` and
// `/api/departments` in parallel and fuses them into the universal
// directory row shape. There is no humans-list endpoint yet, so the
// real-data path renders agents only; humans appear when a future
// fused endpoint lands (tracked as a backlog item — see umbrella spec
// JOBE-0162 §"Wire shape — option C → followup").
//
// Toggle the flag at /admin/feature-flags ("WCC — organization
// directory") to flip between modes at runtime.

import { useEffect, useState } from 'react';
import { flaggedFetch } from '../lib/flags.js';
import { useFlag } from '../lib/useFlagStore.js';

/**
 * @typedef {object} OrganizationRow
 * @property {'human'|'agent'} type
 * @property {string} id              slug, stable across surfaces
 * @property {string} displayName     title-cased proper noun
 * @property {string} role
 * @property {string} dept            slug (research|finance|platform|executive|legal|unassigned)
 * @property {string|null} ownerId    ownedBy relation as scalar slug, or null
 * @property {'Available'|'Degraded'|'Disabled'|null} phase
 * @property {string} [email]
 * @property {string} [slack]
 * @property {number} [evalPassRate]
 * @property {number} [promptVersion]
 */

/**
 * Hook returning the universal directory rows.
 *
 * @returns {{ rows: OrganizationRow[], loading: boolean, mock: boolean, error: string|null }}
 */
export function useOrganizationPeople() {
  // Read the flag's boolean (stable primitive) rather than the overrides
  // object — Zustand recreates the object on every store mutation, which
  // would re-fire the useEffect below on every render.
  const mockOn = useFlag('wcc.mock.organization');
  const [state, setState] = useState({ rows: [], loading: true, mock: false, error: null });

  useEffect(() => {
    let cancelled = false;

    // Mock branch — flaggedFetch walks the FIXTURES map.
    if (mockOn) {
      const fixture = flaggedFetch('wcc.mock.organization', { 'wcc.mock.organization': true });
      if (fixture !== undefined) {
        validateRows(fixture);
        setState({ rows: fixture, loading: false, mock: true, error: null });
        return;
      }
    }

    // Real-data branch — fetch agents + departments in parallel.
    setState((prev) => ({ ...prev, loading: true, mock: false, error: null }));
    // Three-step fetch: agents + departments (parallel), then per-dept rosters.
    // /api/agents      → display name + phase per agent (no dept)
    // /api/departments → dept list (urn + payload + slug)
    // /api/departments/{slug}/roster → { members: [{agent: urn, role: urn}] }
    //   The roster endpoint is the agent→dept join. Per-dept errors are
    //   non-fatal: missing roster just means no dept assignment for that
    //   dept's agents (they land in "Unassigned").
    (async () => {
      try {
        const [agentsRaw, deptsRaw] = await Promise.all([
          fetch('/api/agents', { credentials: 'same-origin' }).then(safeJSON),
          fetch('/api/departments', { credentials: 'same-origin' }).then(safeJSON),
        ]);
        if (cancelled) return;
        const deptList = normalizeDeptItems(deptsRaw);
        const rosterPairs = await Promise.all(
          deptList.map((d) =>
            fetch(`/api/departments/${encodeURIComponent(d.slug)}/roster`, { credentials: 'same-origin' })
              .then(safeJSON)
              .then((body) => ({ slug: d.slug, members: (body && body.members) || [] }))
              // eslint-disable-next-line no-unused-vars
              .catch((_e) => ({ slug: d.slug, members: [] })),
          ),
        );
        if (cancelled) return;
        const rows = fuseRosterFromWire(agentsRaw, deptList, rosterPairs);
        setState({ rows, loading: false, mock: false, error: null });
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[organization] fetch failed', err);
        setState({ rows: [], loading: false, mock: false, error: String(err) });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mockOn]);

  return state;
}

async function safeJSON(r) {
  if (!r.ok) throw new Error(`HTTP ${r.status} for ${r.url}`);
  return r.json();
}

/**
 * Fuse `/api/agents` + `/api/departments` + per-dept rosters into the
 * unified row shape.
 *
 * Wire shapes:
 *   /api/agents      → [{ name, urn, displayName, phase, conversational }]
 *   /api/departments → { items: [{ urn, payload: { displayName, status, lead, ... } }], next_cursor }
 *                       (or a bare array on older deployments)
 *   /api/departments/{slug}/roster → { members: [{ agent: urn, role: urn }] }
 *
 * Agent→dept membership is computed by inverting the per-dept rosters:
 * for each (dept, members[]) pair, record `agent.slug → dept.slug` in a
 * lookup map. Agents missing from every roster land in `'unassigned'`.
 *
 * `agent.role` is sourced from the first role URN observed in the
 * roster pair (`urn:jobe:agent-role:<slug>` → slug). Agents with no
 * dept membership keep the generic `'Agent'` role.
 *
 * @param {unknown} agentsRaw
 * @param {Array<{slug: string, displayName?: string}>} [deptList]  Normalized dept list
 * @param {Array<{slug: string, members: Array<{agent: string, role: string}>}>} [rosterPairs]
 * @returns {OrganizationRow[]}
 */
export function fuseRosterFromWire(agentsRaw, deptList = [], rosterPairs = []) {
  const agents = Array.isArray(agentsRaw) ? agentsRaw : [];
  // Build agent-slug → { dept, role } map from per-dept rosters.
  /** @type {Map<string, { dept: string, role: string }>} */
  const membership = new Map();
  for (const pair of rosterPairs) {
    if (!pair || !Array.isArray(pair.members)) continue;
    for (const m of pair.members) {
      const agentSlug = slugFromURN(m.agent);
      if (!agentSlug) continue;
      // First-wins: an agent appearing in two dept rosters keeps the
      // first one encountered. Multi-dept membership is rare and the
      // proposal's display model is one-dept-per-row.
      if (membership.has(agentSlug)) continue;
      membership.set(agentSlug, {
        dept: pair.slug,
        role: roleFromURN(m.role),
      });
    }
  }
  // deptList is retained for future use (e.g. surfacing dept displayName
  // alongside slug); not consumed in this pass.
  void deptList;
  return agents.map((a) => {
    const slug = a.name || slugFromURN(a.urn || '');
    const m = membership.get(slug);
    return {
      type: 'agent',
      id: slug,
      displayName: a.displayName || titleCase(slug),
      role: m ? titleCase(m.role) : 'Agent',
      dept: m ? m.dept : 'unassigned',
      ownerId: null,
      phase: normalizePhase(a.phase),
    };
  });
}

/**
 * Normalize the /api/departments response into a list of {slug, displayName}.
 * Tolerates both `{items: [...]}` (paged wire shape) and a bare array
 * (older deployments).
 */
export function normalizeDeptItems(raw) {
  const items = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.items) ? raw.items : []);
  return items.map((it) => {
    const urn = (it && it.urn) || '';
    const slug = slugFromURN(urn);
    const payload = (it && it.payload) || {};
    return { slug, displayName: payload.displayName || slug, urn };
  }).filter((d) => d.slug);
}

function slugFromURN(urn) {
  if (!urn) return '';
  const i = urn.lastIndexOf(':');
  return i === -1 ? urn : urn.slice(i + 1);
}

/**
 * Strip role URN prefixes so the role displays as e.g. "Finance Lead"
 * not "urn:jobe:agent-role:finance-lead". Handles both bare-slug and
 * urn-form values.
 */
function roleFromURN(roleURN) {
  if (!roleURN) return '';
  return slugFromURN(roleURN);
}

function titleCase(slug) {
  if (!slug) return '';
  return slug
    .split(/[-_]/)
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ''))
    .join(' ');
}

function normalizePhase(phase) {
  if (!phase) return null;
  const p = String(phase).toLowerCase();
  if (p === 'available' || p === 'running' || p === 'ready' || p === 'active') return 'Available';
  if (p === 'degraded' || p === 'warn') return 'Degraded';
  if (p === 'disabled' || p === 'failed' || p === 'stopped') return 'Disabled';
  return null;
}

/**
 * Dev-only shape guard. Skipped in production builds.
 */
export function validateRows(rows) {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') {
    return;
  }
  for (const r of rows) {
    if (!r || !r.type || !r.id || !r.displayName || !r.role || !r.dept) {
      // eslint-disable-next-line no-console
      console.warn('[organization] malformed row', r);
    }
  }
}
